import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { auth, db } from '../lib/firebase';
import { setPendingInvite, clearPendingInvite, getPendingInvite } from '../lib/pendingInvite';
import { cn } from '../lib/utils';
import KodaCareLogoFull from '../components/Custom Icons/KodaCareLogoFull';
import { SubscriptionGateModal } from '../components/SubscriptionGateModal';
import { useLanguage } from '../functions/useLanguage';
import AppButton from '../components/AppButton';

// On the marketing site (kodacare.pet) the SPA has no /login route, so we
// must redirect to the full app URL instead of navigating inside the router.
const APP_HOST = import.meta.env.VITE_APP_HOST || 'https://app.kodacare.pet';
function isWebsiteHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'kodacare.pet' || h === 'www.kodacare.pet' || h.includes('kodacare-website');
}

type ViewState = 'loading' | 'verifySuccess' | 'recoverSuccess' | 'resetForm' | 'resetSuccess' | 'error' | 'inviteSuccess' | 'inviteError';

function mapActionError(code: string, t: (key: any) => string): string {
  switch (code) {
    case 'auth/expired-action-code':
      return t('authAction.error.expired');
    case 'auth/invalid-action-code':
      return t('authAction.error.invalid');
    case 'auth/user-disabled':
      return t('authAction.error.userDisabled');
    case 'auth/user-not-found':
      return t('authAction.error.userNotFound');
    case 'auth/weak-password':
      return t('authAction.error.weakPassword');
    default:
      return t('authAction.error.generic');
  }
}

export default function AuthAction() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');
  // Co-carer invite params
  const inviteToken = searchParams.get('token');
  const inviteId = searchParams.get('inviteId');
  const invitePetId = searchParams.get('petId');
  const inviteOwnerUid = searchParams.get('ownerUid');

  const [state, setState] = useState<ViewState>('loading');
  const [error, setError] = useState('');
  const [emailForReset, setEmailForReset] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [processedActionKey, setProcessedActionKey] = useState<string | null>(null);
  const [gateModalOpen, setGateModalOpen] = useState(false);
  const inviteUnsubRef = useRef<null | (() => void)>(null);

  const obfuscatedOob = oobCode ? `${oobCode.slice(0, 6)}…(${oobCode.length})` : null;

  useEffect(() => {
    console.info('[AuthAction] init', {
      mode,
      hasOobCode: Boolean(oobCode),
      oobPreview: obfuscatedOob,
      continueUrl,
      href: window.location.href,
    });
  }, [mode, oobCode, obfuscatedOob, continueUrl]);

  const safeContinueUrl = useMemo(() => {
    if (!continueUrl) return '/login';
    try {
      const parsed = new URL(continueUrl, window.location.origin);
      if (parsed.hostname === 'kodacare.pet' || parsed.hostname === 'www.kodacare.pet') {
        return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/login';
      }
      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/login';
      }
      return '/login';
    } catch {
      return '/login';
    }
  }, [continueUrl]);

  useEffect(() => {
    let cancelled = false;
    const actionKey = mode && oobCode ? `${mode}:${oobCode}` : null;

    if (actionKey && processedActionKey === actionKey) {
      console.info('[AuthAction] skipping already-processed action', { actionKey });
      return;
    }

    async function runAction() {
      // ── Co-carer invite flow ──────────────────────────────────────────────
      if (mode?.toLowerCase() === 'acceptpetinvite') {
        if (!inviteToken || !invitePetId || !inviteOwnerUid) {
          if (!cancelled) { setError('Missing invite parameters.'); setState('inviteError'); }
          return;
        }

        // Stash the full invite URL so it survives the
        // register → verify-email → login round-trip (the verification
        // email is sent to a static AUTH_ACTION_URL and would otherwise
        // strip the invite params).
        setPendingInvite(window.location.href);

        const currentUser = auth.currentUser;
        if (!currentUser) {
          // Most invitees don't yet have an account — default to register.
          // Register screen exposes a “Sign in” link that preserves continueUrl.
          const currentUrl = encodeURIComponent(window.location.href);
          navigate(`/register?continueUrl=${currentUrl}`);
          return;
        }

        try {
          const claimRef = doc(db, 'users', currentUser.uid, 'invite_claims', inviteToken);
          const claimPath = `users/${currentUser.uid}/invite_claims/${inviteToken}`;
          console.info('[AuthAction] invite: subscribing to claim doc', { claimPath });

          inviteUnsubRef.current = onSnapshot(
            claimRef,
            (snap) => {
              if (cancelled) return;
              const data = snap.data();
              console.info('[AuthAction] invite: snapshot', { exists: snap.exists(), status: data?.status, error: data?.error });
              if (data?.status === 'success') {
                clearPendingInvite();
                setState('inviteSuccess');
                inviteUnsubRef.current?.();
                inviteUnsubRef.current = null;
              } else if (data?.status === 'error') {
                clearPendingInvite();
                const errorMsg = data.error || 'Failed to accept invite.';
                
                // Check if error is a subscription gate error
                if (errorMsg && errorMsg.includes('plan owner') && errorMsg.includes('subscription')) {
                  console.info('[AuthAction] invite: subscription gate triggered');
                  setGateModalOpen(true);
                  setState('inviteError');
                } else {
                  setError(errorMsg);
                  setState('inviteError');
                }
                
                inviteUnsubRef.current?.();
                inviteUnsubRef.current = null;
              }
            },
            (err) => {
              console.error('[AuthAction] invite: snapshot error', err);
              if (!cancelled) {
                setError(err.message || 'Permission denied reading invite claim.');
                setState('inviteError');
              }
            }
          );

          console.info('[AuthAction] invite: writing claim doc', {
            claimPath, petId: invitePetId, ownerUid: inviteOwnerUid,
          });
          await setDoc(claimRef, {
            inviteToken,
            inviteId: inviteId || '',
            petId: invitePetId,
            ownerUid: inviteOwnerUid,
            callerEmail: currentUser.email || '',
            status: 'pending',
            createdAt: new Date().toISOString()
          });
          console.info('[AuthAction] invite: claim doc written; awaiting trigger');

        } catch (err) {
          console.error('[AuthAction] invite: setDoc/subscribe failed', err);
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to accept invite.');
            setState('inviteError');
          }
        }
        return;
      }

      if (!mode || !oobCode) {
        console.warn('[AuthAction] missing params', { mode, hasOobCode: Boolean(oobCode), href: window.location.href });
        if (!cancelled) {
          setError(t('authAction.error.missingInfo'));
          setState('error');
        }
        return;
      }

      try {
        const normalizedMode = mode.toLowerCase();
        console.info('[AuthAction] processing action', { mode, normalizedMode, oobPreview: obfuscatedOob });

        if (normalizedMode === 'resetpassword') {
          console.info('[AuthAction] validating reset code');
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!cancelled) {
            console.info('[AuthAction] reset code valid', { email });
            setEmailForReset(email);
            setState('resetForm');
          }
          return;
        }

        // For all other OOB code actions (email verify / change-email verify /
        // recover email and compatible future Firebase action modes), attempt
        // to apply the action code directly.
        console.info('[AuthAction] applying action code');
        await applyActionCode(auth, oobCode);
        if (!cancelled) {
          console.info('[AuthAction] action code applied', { mode: normalizedMode });
          if (actionKey) setProcessedActionKey(actionKey);
          setState(normalizedMode === 'recoveremail' ? 'recoverSuccess' : 'verifySuccess');
        }
      } catch (err) {
        const normalizedMode = (mode ?? '').toLowerCase();
        const firebaseCode = err instanceof FirebaseError ? err.code : '';

        // Some email clients/security scanners pre-open verification links,
        // which consumes the OOB code before the user clicks it.
        // For verify-and-change-email, treat already-consumed/expired codes as
        // success to avoid bouncing the user to "Link unavailable".
        if (
          normalizedMode === 'verifyandchangeemail' &&
          (firebaseCode === 'auth/invalid-action-code' || firebaseCode === 'auth/expired-action-code')
        ) {
          console.warn('[AuthAction] verifyAndChangeEmail code already consumed; treating as success', {
            mode,
            oobPreview: obfuscatedOob,
            firebaseCode,
          });
          if (!cancelled) {
            if (actionKey) setProcessedActionKey(actionKey);
            setState('verifySuccess');
          }
          return;
        }

        console.error('[AuthAction] action failed', {
          mode,
          oobPreview: obfuscatedOob,
          error: err,
          firebaseCode: err instanceof FirebaseError ? err.code : undefined,
          firebaseMessage: err instanceof FirebaseError ? err.message : undefined,
        });
        const msg = err instanceof FirebaseError ? mapActionError(err.code, t) : mapActionError('unknown', t);
        if (!cancelled) {
          setError(msg);
          setState('error');
        }
      }
    }

    void runAction();
    return () => {
      cancelled = true;
      inviteUnsubRef.current?.();
      inviteUnsubRef.current = null;
    };
  }, [mode, oobCode, t, processedActionKey]);

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!oobCode) {
      setError(t('authAction.error.resetInvalid'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('authAction.error.passwordMin'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('authAction.error.passwordMismatch'));
      return;
    }

    setSaving(true);
    try {
      console.info('[AuthAction] confirming password reset');
      await confirmPasswordReset(auth, oobCode, newPassword);
      console.info('[AuthAction] password reset success');
      setState('resetSuccess');
    } catch (err) {
      console.error('[AuthAction] password reset failed', {
        oobPreview: obfuscatedOob,
        error: err,
        firebaseCode: err instanceof FirebaseError ? err.code : undefined,
        firebaseMessage: err instanceof FirebaseError ? err.message : undefined,
      });
      const msg = err instanceof FirebaseError ? mapActionError(err.code, t) : mapActionError('unknown', t);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // On the marketing website host there is no /login route, so after a
  // successful email verification we redirect straight to the app.
  useEffect(() => {
    if (state !== 'verifySuccess' || !isWebsiteHost()) return;
    const pending = getPendingInvite();
    const loginUrl = pending
      ? `${APP_HOST}/login?continueUrl=${encodeURIComponent(pending)}`
      : `${APP_HOST}/login`;
    const timer = setTimeout(() => { window.location.href = loginUrl; }, 2000);
    return () => clearTimeout(timer);
  }, [state]);

  const errorPrimaryLink = mode === 'resetPassword' ? '/forgot-password' : '/login';
  const errorPrimaryLabel = useMemo(() => 
    mode === 'resetPassword'
      ? t('authAction.requestNewResetLink')
      : t('authAction.goToSignIn'),
    [mode, t]
  );

  return (
    <div className={cn('min-h-screen flex flex-col items-center justify-center p-4', 'bg-background-light')}>
      <div className={cn('w-full max-w-md rounded-3xl overflow-hidden', 'bg-background-light')}>
        <div className={cn('p-12 text-center', 'bg-background-light')}>
          <div className="w-72 h-auto flex items-center justify-center mx-auto mb-0">
            <KodaCareLogoFull height={55} className="w-full h-auto text-primary-brown" />
          </div>
        </div>

        <div className="p-8">
          {state === 'loading' && (
            <div className="text-center">
              <div className="mx-auto mb-4 w-7 h-7 border-2 border-primary-brown border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-text-secondary">{t('authAction.loading')}</p>
            </div>
          )}

          {state === 'verifySuccess' && (() => {
            const pending = getPendingInvite();
            const onWebsite = isWebsiteHost();
            const signInHref = pending
              ? (onWebsite ? `${APP_HOST}/login?continueUrl=${encodeURIComponent(pending)}` : `/login?continueUrl=${encodeURIComponent(pending)}`)
              : (onWebsite ? `${APP_HOST}/login` : '/login');
            const btnClass = 'inline-flex w-full items-center justify-center p-3 rounded-xl font-bold transition-colors bg-primary-brown text-white hover:bg-primary-brown';
            return (
              <div className="text-center space-y-3">
                <h2 className="text-xl font-bold text-text-primary">{t('authAction.verifySuccessTitle')}</h2>
                <p className="text-sm text-text-secondary">{t('authAction.verifySuccessBody')}</p>
                {onWebsite ? (
                  <a href={signInHref} className={btnClass}>{t('authAction.goToSignIn')}</a>
                ) : (
                  <Link to={signInHref} className={btnClass}>{t('authAction.goToSignIn')}</Link>
                )}
              </div>
            );
          })()}

          {state === 'recoverSuccess' && (
            <div className="text-center space-y-3">
              <h2 className="text-xl font-bold text-text-primary">{t('authAction.recoverSuccessTitle')}</h2>
              <p className="text-sm text-text-secondary">{t('authAction.recoverSuccessBody')}</p>
              <Link
                to="/login"
                className="inline-flex w-full items-center justify-center p-3 rounded-xl font-bold transition-colors bg-primary-brown text-white hover:bg-primary-brown"
              >
                {t('authAction.goToSignIn')}
              </Link>
            </div>
          )}

          {state === 'resetForm' && (
            <form onSubmit={submitReset} className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-text-primary">{t('authAction.resetTitle')}</h2>
                {emailForReset && (
                  <p className="mt-1 text-sm text-text-secondary">{t('authAction.resetBody').replace('{email}', emailForReset)}</p>
                )}
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 text-text-secondary">{t('authAction.newPassword')}</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-text-secondary">{t('authAction.confirmNewPassword')}</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40"
                  placeholder="••••••••"
                />
              </div>

              <AppButton
                type="submit"
                disabled={saving}
                className="p-4"
              >
                {saving ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('authAction.updatePassword')}
              </AppButton>
            </form>
          )}

          {state === 'resetSuccess' && (
            <div className="text-center space-y-3">
              <h2 className="text-xl font-bold text-text-primary">{t('authAction.resetSuccessTitle')}</h2>
              <p className="text-sm text-text-secondary">{t('authAction.resetSuccessBody')}</p>
              <Link
                to="/login"
                className="inline-flex w-full items-center justify-center p-3 rounded-xl font-bold transition-colors bg-primary-brown text-white hover:bg-primary-brown"
              >
                {t('authAction.signIn')}
              </Link>
            </div>
          )}

          {state === 'error' && (
            <div className="text-center space-y-3">
              <h2 className="text-xl font-bold text-text-primary">{t('authAction.linkUnavailableTitle')}</h2>
              <p className="text-sm text-error-red">{error}</p>
              <Link
                to={errorPrimaryLink}
                className="inline-flex w-full items-center justify-center p-3 rounded-xl font-bold transition-colors bg-primary-brown text-white hover:bg-primary-brown"
              >
                {errorPrimaryLabel}
              </Link>
              <Link to="/login" className="inline-block text-sm font-medium text-primary-brown hover:underline">
                {t('authAction.backToSignIn')}
              </Link>
            </div>
          )}

          {state === 'inviteSuccess' && (
            <div className="text-center space-y-3">
              <h2 className="text-xl font-bold text-text-primary">You're in! 🐾</h2>
              <p className="text-sm text-text-secondary">You've been added as a co-carer. You can now view and log care for this pet.</p>
              <Link to="/records" className="block mt-4 text-sm font-semibold text-primary-brown hover:underline">
                Go to Records →
              </Link>
            </div>
          )}

          {state === 'inviteError' && (
            <div className="text-center space-y-3">
              <h2 className="text-xl font-bold text-error-red">Invite failed</h2>
              <p className="text-sm text-text-secondary">{error || 'This invite link is invalid or has already been used.'}</p>
              <Link to="/login" className="block mt-4 text-sm font-semibold text-primary-brown hover:underline">
                {t('authAction.backToSignIn')}
              </Link>
            </div>
          )}

          {!['loading', 'verifySuccess', 'recoverSuccess', 'resetForm', 'resetSuccess', 'error', 'inviteSuccess', 'inviteError'].includes(state) && (
            <div className="text-center">
              <div className="mx-auto mb-4 w-7 h-7 border-2 border-primary-brown border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-text-secondary">Loading…</p>
            </div>
          )}
        </div>
      </div>

      {/* Subscription gate modal for co-carers trying to accept ownership */}
      <SubscriptionGateModal
        open={gateModalOpen}
        onClose={() => setGateModalOpen(false)}
        action="accept_ownership"
      />
    </div>
  );
}
